import React from "react";
import {
  Box, Container, Text, Flex, VStack, Link, HStack,
} from "@chakra-ui/react";
import { Mail, AtSign } from "lucide-react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const ContactCard = ({ icon: Icon, label, value, href, delay }) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.45, delay }}
    flex="1"
    minW={{ base: "100%", sm: "260px" }}
    maxW="340px"
  >
    <Link href={href} target="_blank" rel="noopener noreferrer" _hover={{ textDecoration: "none" }}>
      <Box
        p={7}
        bg="white"
        borderRadius="16px"
        border="1px solid rgba(0,149,200,0.15)"
        boxShadow="0 4px 24px rgba(41,52,82,0.07)"
        _hover={{
          borderColor: "#0095C8",
          boxShadow: "0 8px 36px rgba(0,149,200,0.14)",
          transform: "translateY(-3px)",
        }}
        transition="all 0.25s cubic-bezier(0.4,0,0.2,1)"
        textAlign="center"
      >
        <Flex
          w="52px"
          h="52px"
          borderRadius="12px"
          bg="rgba(0,149,200,0.1)"
          align="center"
          justify="center"
          mx="auto"
          mb={4}
        >
          <Icon size={22} color="#0095C8" />
        </Flex>
        <Text
          fontFamily="'Inter', sans-serif"
          fontSize="0.78rem"
          fontWeight="600"
          letterSpacing="0.1em"
          textTransform="uppercase"
          color="rgba(41,52,82,0.45)"
          mb={1}
        >
          {label}
        </Text>
        <Text
          fontFamily="'Inter', sans-serif"
          fontSize="0.9375rem"
          fontWeight="600"
          color="#0095C8"
        >
          {value}
        </Text>
      </Box>
    </Link>
  </MotionBox>
);

export const Contact = () => {
  return (
    <Box
      id="contact"
      bg="#f8fafc"
      py={{ base: "80px", md: "112px" }}
      position="relative"
      overflow="hidden"
    >
      {/* Subtle background accent */}
      <Box
        position="absolute"
        top="-120px"
        right="-120px"
        w={{ base: "320px", md: "480px" }}
        h={{ base: "320px", md: "480px" }}
        borderRadius="50%"
        border="60px solid rgba(0,149,200,0.06)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-80px"
        left="-80px"
        w={{ base: "240px", md: "360px" }}
        h={{ base: "240px", md: "360px" }}
        borderRadius="50%"
        border="44px solid rgba(0,149,200,0.05)"
        pointerEvents="none"
      />

      <Container maxW="1200px" px={{ base: "5", md: "8" }} position="relative" zIndex={1}>
        <VStack spacing={{ base: 10, md: 14 }} align="center">

          {/* Heading */}
          <MotionBox
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            textAlign="center"
          >
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize="0.8rem"
              fontWeight="600"
              letterSpacing="0.12em"
              textTransform="uppercase"
              color="#0095C8"
              mb={3}
            >
              Get in Touch
            </Text>
            <Text
              as="h2"
              fontFamily="'Public Sans', sans-serif"
              fontWeight="700"
              fontSize={{ base: "2rem", md: "2.75rem" }}
              color="#293452"
              lineHeight="1.2"
              maxW="520px"
            >
              Ready to get{" "}
              <Box as="span" bgGradient="linear(135deg, #0095C8, #809CFF)" bgClip="text">
                started?
              </Box>
            </Text>
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize={{ base: "0.9375rem", md: "1.0625rem" }}
              color="rgba(41,52,82,0.55)"
              mt={4}
              maxW="440px"
              mx="auto"
              lineHeight="1.7"
            >
              Reach out to us and we'll get you set up. No pressure, just answers.
            </Text>
          </MotionBox>

          {/* Contact cards */}
          <Flex
            gap={6}
            justify="center"
            flexWrap="wrap"
            w="full"
          >
            <ContactCard
              icon={Mail}
              label="Email us"
              value="developer@31gauge.com"
              href="mailto:developer@31gauge.com"
              delay={0.1}
            />
            <ContactCard
              icon={AtSign}
              label="Instagram"
              value="@31gaugecentral"
              href="https://www.instagram.com/31gaugecentral?igsh=aDRtaThvOTVkdDJ3"
              delay={0.2}
            />
          </Flex>

          {/* Reassurance line */}
          <MotionBox
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <HStack spacing={2} justify="center">
              <Box w="6px" h="6px" borderRadius="50%" bg="#0095C8" flexShrink={0} />
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize="0.875rem"
                color="rgba(41,52,82,0.45)"
              >
                We typically respond within 24 hours
              </Text>
            </HStack>
          </MotionBox>

        </VStack>
      </Container>
    </Box>
  );
};
